import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, NgZone,
  OnChanges, OnDestroy, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import type * as THREE_NS from 'three';
import {
  convexHull3D, elevationColor, tileBbox, toLocalMeters, VOLUME_MIN_POINTS,
} from '../../helpers/volume-geometry';

export type VolumeBasemap = 'satellite' | 'streets' | 'topo';

type Three = typeof THREE_NS;

const BASEMAP_CYCLE: VolumeBasemap[] = ['satellite', 'streets', 'topo'];
const TILE_SERVICE: Record<VolumeBasemap, string> = {
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export',
  streets: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/export',
  topo: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/export',
};
/** Sharp tile for the final ground plane. */
const TILE_SIZE = 1024;
/** Small tile painted first — the 1024 export takes ESRI several seconds to render. */
const TILE_PREVIEW_SIZE = 512;
const TILE_CACHE = new Map<string, HTMLImageElement>();
const TILE_CACHE_MAX = 24;
const METERS_PER_DEGREE_LAT = 111320;

/**
 * 3D volume viewer — port of mobile's Geometry3DViewer. Convex hull of the captured points
 * over an ESRI ground tile, with orbit (drag), zoom (wheel / pinch), compass and basemap toggle.
 * three.js is loaded lazily so it stays out of the main bundle.
 */
@Component({
  selector: 'app-volume-viewer-3d',
  standalone: true,
  imports: [],
  templateUrl: './volume-viewer-3d.component.html',
  styleUrls: ['./volume-viewer-3d.component.scss'],
})
export class VolumeViewer3dComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Overridable so specs can simulate a browser without WebGL / three.js. */
  static loadThree: () => Promise<Three> = () => import('three') as Promise<Three>;

  @Input() coordinates: number[][] = [];
  @Input() volumeLabel = '';
  @Input() basemap: VolumeBasemap = 'satellite';
  @Input() showLayersToggle = true;
  /** Full-viewport overlay layout (40px insets, close on the right) — matches the map picture overlay. */
  @Input() overlay = false;
  /** Emitted when the ✕ is clicked; the ✕ only renders when someone listens. */
  @Output() closed = new EventEmitter<void>();
  @ViewChild('canvasHost', { static: true }) canvasHost: ElementRef<HTMLDivElement>;

  readonly basemapShortLabel: Record<VolumeBasemap, string> = { satellite: 'SAT', streets: 'STR', topo: 'TOPO' };
  activeBasemap: VolumeBasemap = 'satellite';
  heading = 0;
  unavailable = false;
  noElevation = false;
  dragging = false;

  private three: Three | null = null;
  private scene: THREE_NS.Scene;
  private camera: THREE_NS.PerspectiveCamera;
  private renderer: THREE_NS.WebGLRenderer;
  private dynamicGroup: THREE_NS.Group;
  private groundGroup: THREE_NS.Group;
  private center: THREE_NS.Vector3;
  private theta = Math.PI / 4;
  private phi = Math.PI / 2.5;
  private radius = 10;
  private lastBearing = -999;
  private pendingTile: { sizeX: number; sizeZ: number; cx: number; cz: number } | null = null;
  private tileRequest = 0;
  private frameId = 0;
  private resizeObserver: ResizeObserver | null = null;
  private cleanups: (() => void)[] = [];
  private destroyed = false;

  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.basemap && this.basemap) this.activeBasemap = this.basemap;
    if (!this.three) return;
    if (changes.coordinates && !changes.coordinates.firstChange) {
      this.buildScene();
      this.loadTile();
    } else if (changes.basemap && !changes.basemap.firstChange) {
      this.loadTile();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    this.activeBasemap = this.basemap;
    try {
      const three = await VolumeViewer3dComponent.loadThree();
      if (this.destroyed) return;
      this.three = three;
      this.zone.runOutsideAngular(() => this.initScene());
      this.buildScene();
      this.loadTile();
    } catch {
      this.unavailable = true;
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.frameId) cancelAnimationFrame(this.frameId);
    this.resizeObserver?.disconnect();
    this.cleanups.forEach(fn => fn());
    this.cleanups = [];
    if (this.three) {
      this.clearGroup(this.dynamicGroup);
      this.clearGroup(this.groundGroup);
      (this.scene.background as THREE_NS.Texture | null)?.dispose?.();
      this.renderer?.dispose();
      this.renderer?.domElement?.remove();
    }
  }

  cycleBasemap(): void {
    const next = BASEMAP_CYCLE[(BASEMAP_CYCLE.indexOf(this.activeBasemap) + 1) % BASEMAP_CYCLE.length];
    this.activeBasemap = next;
    if (this.three) this.loadTile();
  }

  // ---------------------------------------------------------------- scene setup

  private initScene(): void {
    const THREE = this.three;
    const host = this.canvasHost.nativeElement;
    const width = host.clientWidth || 1;
    const height = host.clientHeight || 1;

    this.scene = new THREE.Scene();
    const sky = document.createElement('canvas');
    sky.width = 4; sky.height = 512;
    const ctx = sky.getContext('2d');
    const grad = ctx.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0, '#4a78a8');
    grad.addColorStop(0.55, '#87a8c8');
    grad.addColorStop(0.85, '#c8d5dd');
    grad.addColorStop(1, '#e0e4e0');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 4, 512);
    this.scene.background = new THREE.CanvasTexture(sky);
    this.scene.fog = new THREE.Fog(0xc8d5dd, 1, 200);

    this.camera = new THREE.PerspectiveCamera(55, width / height, 0.01, 1000);
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    host.appendChild(this.renderer.domElement);

    this.scene.add(new THREE.AmbientLight(0x8ea3b8, 0.9));
    const sun = new THREE.DirectionalLight(0xfff2d5, 0.9);
    sun.position.set(10, 20, 8);
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight(0x8fb8e0, 0.25);
    fill.position.set(-8, 6, -6);
    this.scene.add(fill);

    this.dynamicGroup = new THREE.Group();
    this.groundGroup = new THREE.Group();
    this.scene.add(this.dynamicGroup, this.groundGroup);
    this.center = new THREE.Vector3();

    this.bindControls(this.renderer.domElement);

    this.resizeObserver = new ResizeObserver(() => {
      const w = host.clientWidth || 1;
      const h = host.clientHeight || 1;
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h);
    });
    this.resizeObserver.observe(host);

    const animate = () => {
      if (this.destroyed) return;
      this.frameId = requestAnimationFrame(animate);
      this.renderer.render(this.scene, this.camera);
    };
    animate();
  }

  private clearGroup(group: THREE_NS.Group | undefined): void {
    if (!group) return;
    while (group.children.length) {
      const child = group.children[0];
      this.disposeObject(child);
      group.remove(child);
    }
  }

  /** Free GPU resources of a mesh / line / helper (geometry, materials, texture maps). */
  private disposeObject(obj: THREE_NS.Object3D): void {
    const mesh = obj as THREE_NS.Mesh;
    mesh.geometry?.dispose?.();
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materials.forEach(m => {
      (m as THREE_NS.MeshBasicMaterial | undefined)?.map?.dispose?.();
      m?.dispose?.();
    });
  }

  private buildScene(): void {
    const THREE = this.three;
    const raw = this.coordinates || [];
    this.clearGroup(this.dynamicGroup);
    this.clearGroup(this.groundGroup);
    this.pendingTile = null;
    if (raw.length === 0) return;

    const local = toLocalMeters(raw);
    const pts = local.points;
    const range = (local.maxAlt - local.minAlt) || 1;
    const colorFor = (alt: number | null) => {
      const [r, g, b] = elevationColor(alt != null ? (alt - local.minAlt) / range : 0);
      return new THREE.Color(r, g, b);
    };

    this.noElevation = !local.hasZData || raw.length < VOLUME_MIN_POINTS;
    const faces = this.noElevation ? [] : convexHull3D(pts);

    if (faces.length) {
      const verts: number[] = [];
      const cols: number[] = [];
      const wire: number[] = [];
      for (const f of faces) {
        const a = pts[f.a], b = pts[f.b], c = pts[f.c];
        verts.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
        for (const p of [a, b, c]) { const col = colorFor(p.alt); cols.push(col.r, col.g, col.b); }
        wire.push(a.x, a.y, a.z, b.x, b.y, b.z, b.x, b.y, b.z, c.x, c.y, c.z, c.x, c.y, c.z, a.x, a.y, a.z);
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
      geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
      geo.computeVertexNormals();
      this.dynamicGroup.add(new THREE.Mesh(geo, new THREE.MeshPhongMaterial({
        vertexColors: true, transparent: true, opacity: 0.75, side: THREE.DoubleSide, shininess: 40,
      })));
      const wireGeo = new THREE.BufferGeometry();
      wireGeo.setAttribute('position', new THREE.Float32BufferAttribute(wire, 3));
      this.dynamicGroup.add(new THREE.LineSegments(wireGeo, new THREE.LineBasicMaterial({
        color: 0xffffff, transparent: true, opacity: 0.35,
      })));
    }

    for (const p of pts) {
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 8), new THREE.MeshBasicMaterial({ color: colorFor(p.alt) }));
      sphere.position.set(p.x, p.y, p.z);
      this.dynamicGroup.add(sphere);
    }

    // Frame the raw cloud (not the hull) so coplanar captures still center correctly.
    let mnX = pts[0].x, mxX = pts[0].x, mnY = pts[0].y, mxY = pts[0].y, mnZ = pts[0].z, mxZ = pts[0].z;
    for (const p of pts) {
      mnX = Math.min(mnX, p.x); mxX = Math.max(mxX, p.x);
      mnY = Math.min(mnY, p.y); mxY = Math.max(mxY, p.y);
      mnZ = Math.min(mnZ, p.z); mxZ = Math.max(mxZ, p.z);
    }
    this.center.set((mnX + mxX) / 2, (mnY + mxY) / 2, (mnZ + mxZ) / 2);
    this.radius = Math.max(Math.max(mxX - mnX, mxY - mnY, mxZ - mnZ) * 3.5, 5);

    const bbox = tileBbox(raw);
    const cosLat = Math.cos((local.center.lat * Math.PI) / 180);
    const sizeX = (bbox.maxLng - bbox.minLng) * METERS_PER_DEGREE_LAT * cosLat;
    const sizeZ = (bbox.maxLat - bbox.minLat) * METERS_PER_DEGREE_LAT;
    this.pendingTile = { sizeX, sizeZ, cx: this.center.x, cz: this.center.z };

    const grid = new THREE.GridHelper(Math.max(sizeX, sizeZ), 20, 0x6f8094, 0x6f8094);
    (grid.material as THREE_NS.Material).transparent = true;
    (grid.material as THREE_NS.Material).opacity = 0.12;
    grid.position.set(this.center.x, -0.04, this.center.z);
    this.groundGroup.add(grid);

    (this.scene.fog as THREE_NS.Fog).far = Math.max(sizeX, sizeZ) * 0.8;
    (this.scene.fog as THREE_NS.Fog).near = this.radius * 0.6;

    this.updateCamera();
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------- ground tile

  private tileUrl(size: number = TILE_SIZE): string | null {
    const raw = this.coordinates || [];
    if (raw.length === 0) return null;
    const b = tileBbox(raw);
    const bbox = `${b.minLng},${b.minLat},${b.maxLng},${b.maxLat}`;
    return `${TILE_SERVICE[this.activeBasemap]}?bbox=${bbox}&bboxSR=4326&size=${size},${size}&format=png&f=image`;
  }

  /**
   * Paint the ground: a cached sharp tile wins immediately; otherwise a fast 512px preview is
   * applied first and the 1024px tile replaces it when it lands (same approach as mobile).
   */
  private loadTile(): void {
    const sharpUrl = this.tileUrl(TILE_SIZE);
    const previewUrl = this.tileUrl(TILE_PREVIEW_SIZE);
    if (!sharpUrl || !previewUrl || !this.pendingTile) return;
    const request = ++this.tileRequest;

    const sharpCached = TILE_CACHE.get(sharpUrl);
    if (sharpCached) { this.applyTile(sharpCached); return; }

    let sharpApplied = false;
    const previewCached = TILE_CACHE.get(previewUrl);
    if (previewCached) {
      this.applyTile(previewCached);
    } else {
      this.fetchTile(previewUrl, img => { if (request === this.tileRequest && !sharpApplied) this.applyTile(img); });
    }
    this.fetchTile(sharpUrl, img => { if (request === this.tileRequest) { sharpApplied = true; this.applyTile(img); } });
  }

  /** Load one tile with anonymous CORS, cache it, retry once after 400 ms on failure. */
  private fetchTile(url: string, onLoaded: (img: HTMLImageElement) => void, retriesLeft = 1): void {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (TILE_CACHE.size >= TILE_CACHE_MAX) {
        const oldest = TILE_CACHE.keys().next().value;
        if (oldest !== undefined) TILE_CACHE.delete(oldest);
      }
      TILE_CACHE.set(url, img);
      if (!this.destroyed) onLoaded(img);
    };
    img.onerror = () => {
      if (retriesLeft > 0 && !this.destroyed) setTimeout(() => this.fetchTile(url, onLoaded, retriesLeft - 1), 400);
    };
    img.src = url;
  }

  private applyTile(img: HTMLImageElement): void {
    const THREE = this.three;
    if (!this.pendingTile) return;
    // Drop the previous tile plane (keep the grid).
    this.groundGroup.children
      .filter(c => c.userData?.tile)
      .forEach(c => { this.disposeObject(c); this.groundGroup.remove(c); });
    const texture = new THREE.Texture(img);
    texture.needsUpdate = true;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(this.pendingTile.sizeX, this.pendingTile.sizeZ),
      new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0.95 }),
    );
    plane.rotation.x = -Math.PI / 2;
    plane.position.set(this.pendingTile.cx, -0.08, this.pendingTile.cz);
    plane.userData.tile = true;
    this.groundGroup.add(plane);
  }

  // ---------------------------------------------------------------- camera + controls

  private updateCamera(): void {
    this.camera.position.set(
      this.center.x + this.radius * Math.sin(this.phi) * Math.cos(this.theta),
      this.center.y + this.radius * Math.cos(this.phi),
      this.center.z + this.radius * Math.sin(this.phi) * Math.sin(this.theta),
    );
    this.camera.lookAt(this.center);
    this.emitHeading();
  }

  private emitHeading(): void {
    const bearing = ((90 - (this.theta * 180) / Math.PI) % 360 + 360) % 360;
    const delta = Math.abs(bearing - this.lastBearing);
    if (delta < 1 && delta < 359) return;
    this.lastBearing = bearing;
    this.zone.run(() => { this.heading = bearing; this.cdr.markForCheck(); });
  }

  private orbit(dx: number, dy: number): void {
    this.theta -= dx * 0.01;
    this.phi = Math.max(0.1, Math.min(Math.PI - 0.1, this.phi - dy * 0.01));
    this.updateCamera();
  }

  private zoomBy(factor: number): void {
    this.radius = Math.max(1, Math.min(200, this.radius * factor));
    this.updateCamera();
  }

  private bindControls(el: HTMLElement): void {
    const pointers = new Map<number, { x: number; y: number }>();
    let pinchDistance = 0;
    const on = <K extends keyof HTMLElementEventMap>(
      type: K,
      fn: (e: HTMLElementEventMap[K]) => void,
      opts?: AddEventListenerOptions,
    ) => {
      el.addEventListener(type, fn, opts);
      this.cleanups.push(() => el.removeEventListener(type, fn, opts));
    };
    on('pointerdown', (e: PointerEvent) => {
      el.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
      }
      this.zone.run(() => { this.dragging = true; this.cdr.markForCheck(); });
    });
    on('pointermove', (e: PointerEvent) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        this.orbit(e.clientX - prev.x, e.clientY - prev.y);
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinchDistance > 0 && d > 0) this.zoomBy(pinchDistance / d);
        pinchDistance = d;
      }
    });
    const release = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchDistance = 0;
      if (pointers.size === 0) this.zone.run(() => { this.dragging = false; this.cdr.markForCheck(); });
    };
    on('pointerup', release);
    on('pointercancel', release);
    on('wheel', (e: WheelEvent) => {
      e.preventDefault();
      this.radius = Math.max(1, Math.min(200, this.radius + e.deltaY * 0.05));
      this.updateCamera();
    }, { passive: false });
  }
}
